import {useState} from 'react'
import {useNavigate, useParams, Link} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {Pencil, Trash2} from 'lucide-react'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {AppLocalDate, currentYearQuarter, formatDate} from '@/lib/date-utils'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {ErrorBanner} from '@/components/ui/error-banner'
import {CampaignFormFields, isCampaignFormValid} from '@/features/certification/components/CampaignFormFields'
import type {ComboboxOption} from '@/components/ui/combobox'
import {Badge} from '@/components/ui/badge'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useServices} from '@/services/context'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {DetailList} from '@/components/shared/DetailList'
import {DetailField} from '@/components/shared/DetailField'
import {EntityLink} from '@/components/shared/EntityLink'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {PageLayout} from '@/components/shared/PageLayout'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {useAppConfig} from '@/hooks/use-app-config'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {WizardStep} from '@/features/certification/hooks/useCertificationPolicyWizardForm'
import {CONCEPT_ICON} from '@/lib/concepts'
import {NonResponseAction, ReviewerType} from '@/types/certification'
import type {CreateCampaignRequest} from '@/types/certification'

interface EditSectionLinkProps {
    to: string
    label: string
    testId: string
}

function EditSectionLink({to, label, testId}: EditSectionLinkProps) {
    return (
        <Button variant="outline" size="sm" asChild data-testid={testId}>
            <Link to={to} data-testid={testId}>
                <IconText icon={Pencil}>{label}</IconText>
            </Link>
        </Button>
    )
}

export function CertificationPolicyDetail() {
    const services = useServices()
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {hasPermission} = useAuth()

    usePageTitle(i18n.certificationPolicies.detailPageTitle)

    const canDelete = hasPermission(permissions.certificationPolicies.delete)
    const canCreateCampaign = hasPermission(permissions.certifications.create)
    const canUpdate = hasPermission(permissions.certificationPolicies.update)
    const {certificationPolicyResponsibilityTypes} = useAppConfig()

    const deleteDialog = useDialog()
    const createCampaignDialog = useDialog()
    const [campaignOwnerSearchQuery, setCampaignOwnerSearchQuery] = useState('')

    const {data: policy, isLoading, error} = useQuery({
        queryKey: ['certification-policy', id],
        queryFn: () => services.certificationPolicies.detail(id!),
        enabled: !!id,
    })

    const {data: campaignSummary} = useQuery({
        queryKey: ['certification-policy-campaign-summary', id],
        queryFn: () => services.certificationPolicies.campaignSummary(id!),
        enabled: !!id,
    })

    const campaignCount = campaignSummary?.totalCampaigns ?? 0
    const hasOpenCampaign = campaignSummary?.hasOpenCampaign ?? false

    const isDefault = policy?.isDefault === true
    const showEdit = canUpdate && !isDefault

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.certificationPolicies.remove(id!),
        invalidate: ['certification-policies'],
        successMessage: i18n.certificationPolicies.policyDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.certificationPolicies.list())
        },
    })

    const [newCampaign, setNewCampaign] = useState<CreateCampaignRequest>({
        name: '',
        description: null,
        dueDate: '',
        ownerPartyId: '',
    })

    const {data: campaignSelectedOwner} = useQuery({
        queryKey: ['parties', 'campaign-owner', newCampaign.ownerPartyId],
        queryFn: () => services.parties.detail(newCampaign.ownerPartyId),
        enabled: !!newCampaign.ownerPartyId,
    })
    const {data: campaignOwnerSearchData, isLoading: campaignOwnerSearchLoading} = useQuery({
        queryKey: ['parties', 'search', campaignOwnerSearchQuery],
        queryFn: () => services.parties.list({q: campaignOwnerSearchQuery, size: 10}),
    })
    const campaignOwnerOptions: ComboboxOption[] = (campaignOwnerSearchData?.content || []).map((p) => ({
        value: p.id,
        label: p.derivedDisplayName,
        description: p.partyType,
    }))

    const generateCampaignName = () => {
        const {year, quarter} = currentYearQuarter()
        // eslint-disable-next-line i18next/no-literal-string
        return `${policy?.name ?? ''} — ${year}-Q${quarter}`
    }

    const generateCampaignDueDate = () => {
        const frequencyDays = policy?.parameter?.reviewFrequencyDays ?? 90
        return AppLocalDate.maxFromToday(frequencyDays)
    }

    const openCreateCampaignDialog = () => {
        setNewCampaign({
            name: generateCampaignName(),
            description: `Generated from policy '${policy?.name ?? ''}'`,
            dueDate: generateCampaignDueDate(),
            ownerPartyId: policy?.parameter?.owner?.id ?? '',
            certificationPolicyId: id,
        })
        createCampaignDialog.open()
    }

    const createCampaignMutation = useEntityMutation({
        mutationFn: (request: CreateCampaignRequest) =>
            services.certificationCampaigns.create(request),
        invalidate: ['certification-campaigns'],
        successMessage: i18n.certificationPolicies.campaignCreatedFromPolicy,
        closeDialog: createCampaignDialog.close,
        onSuccess: (campaign) => {
            navigate(routes.accessGovernance.certificationCampaigns.detail(campaign.id))
        },
    })

    const reviewerTypeLabel = policy?.parameter?.reviewerType === ReviewerType.PROFILE_OWNER
        ? i18n.certificationPolicies.reviewerTypeProfileOwner
        : policy?.parameter?.reviewerType === ReviewerType.RESOURCE_OWNER
            ? i18n.certificationPolicies.reviewerTypeResourceOwner
            : i18n.certificationPolicies.reviewerTypeManager

    const nonResponseLabel = policy?.parameter?.nonResponseAction === NonResponseAction.AUTO_REVOKE
        ? i18n.certificationPolicies.nonResponseAutoRevoke
        : policy?.parameter?.nonResponseAction === NonResponseAction.ESCALATE
            ? i18n.certificationPolicies.nonResponseEscalate
            : i18n.certificationPolicies.nonResponseNoChange

    const scopeIsExpression = policy != null && policy.conditionExpr != null
    const conditionCount = policy?.conditions?.length ?? 0
    const scopeValue = scopeIsExpression
        ? policy?.conditionExpr
        : conditionCount > 0
            ? i18n.certificationPolicies.summary.conditions({count: conditionCount})
            : i18n.certificationPolicies.summary.certifyEverything

    const frequencyValue = policy?.parameter?.reviewFrequencyDays != null
        ? i18n.certificationPolicies.frequencyDays({days: policy.parameter.reviewFrequencyDays})
        : i18n.certificationPolicies.noFrequency


    return (
        <PageLayout
            data-testid="certification-policy-detail-page"
            variant="detail"
            title={policy?.name ?? ''}
            icon={CONCEPT_ICON.certificationCampaign}
            backTo={routes.accessGovernance.certificationPolicies.list()}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(policy, 'CERTIFICATION_POLICY') : undefined)}
            description={campaignCount > 0 ? (
                <Link
                    to={routes.accessGovernance.certificationCampaigns.list()}
                    data-testid="badge-campaign-count"
                >
                    <Badge variant="secondary" className="cursor-pointer">
                        <Icon icon={CONCEPT_ICON.certificationCampaign} size="xs" className="mr-1"/>
                        {i18n.certificationPolicies.campaignCount({count: campaignCount})}
                    </Badge>
                </Link>
            ) : undefined}
            actions={
                <InlineRow>
                    {canCreateCampaign && (
                        <span title={!policy?.active ? String(i18n.certificationPolicies.policyNotActive) : undefined}>
                            <Button
                                variant={policy?.active ? 'default' : 'outline'}
                                onClick={openCreateCampaignDialog}
                                disabled={!policy?.active}
                                data-testid="btn-create-campaign"
                            >
                                <IconText icon={CONCEPT_ICON.certificationCampaign}>{i18n.certificationPolicies.createCampaign}</IconText>
                            </Button>
                        </span>
                    )}
                    {canDelete && !isDefault && (
                        <Button
                            variant="destructive"
                            onClick={deleteDialog.open}
                            data-testid="btn-delete"
                        >
                            <IconText icon={Trash2}>{i18n.common.delete}</IconText>
                        </Button>
                    )}
                </InlineRow>
            }
        >
            <Stack gap="lg">
                {isDefault && (
                    <InfoBanner variant="info" data-testid="info-default-policy">
                        {i18n.certificationPolicies.summary.defaultPolicyNotEditable}
                    </InfoBanner>
                )}

                {canCreateCampaign && hasOpenCampaign && (
                    <InfoBanner variant="warning" data-testid="info-open-campaign-warning">
                        {i18n.certificationPolicies.summary.openCampaignWarning}
                    </InfoBanner>
                )}

                <Card data-testid="section-basics">
                    <RowCardHeader>
                        <CardTitle data-testid="section-basics-title">{i18n.certificationPolicies.summary.basicsSection}</CardTitle>
                        {showEdit && (
                            <EditSectionLink
                                to={routes.accessGovernance.certificationPolicies.editAtStep(id!, WizardStep.BASICS)}
                                label={String(i18n.certificationPolicies.summary.editBasics)}
                                testId="btn-edit-basics"
                            />
                        )}
                    </RowCardHeader>
                    <CardContent>
                        <DetailList columns={2}>
                            <DetailField label={i18n.certificationPolicies.name} value={policy?.name}/>
                            <DetailField label={i18n.certificationPolicies.priority} value={policy?.priority}/>
                            <DetailField label={i18n.certificationPolicies.description} value={policy?.description} fullWidth/>
                            <DetailField label={i18n.certificationPolicies.active} value={policy?.active ? i18n.common.yes : i18n.common.no}/>
                            <DetailField label={i18n.certificationPolicies.summary.validFrom} value={policy?.validFrom ? formatDate(policy.validFrom) : null}/>
                            <DetailField label={i18n.certificationPolicies.summary.validTo} value={policy?.validTo ? formatDate(policy.validTo) : null}/>
                        </DetailList>
                    </CardContent>
                </Card>

                <Card data-testid="section-scope">
                    <RowCardHeader>
                        <CardTitle data-testid="section-scope-title">{i18n.certificationPolicies.summary.scopeSection}</CardTitle>
                        {showEdit && (
                            <EditSectionLink
                                to={routes.accessGovernance.certificationPolicies.editAtStep(id!, WizardStep.SCOPE)}
                                label={String(i18n.certificationPolicies.summary.editScope)}
                                testId="btn-edit-scope"
                            />
                        )}
                    </RowCardHeader>
                    <CardContent>
                        <DetailList columns={2}>
                            <DetailField
                                label={i18n.certificationPolicies.summary.scopeMode}
                                value={scopeIsExpression
                                    ? i18n.certificationPolicies.summary.scopeModeExpression
                                    : i18n.certificationPolicies.summary.scopeModeConditions}
                            />
                            <DetailField
                                label={scopeIsExpression
                                    ? i18n.certificationPolicies.summary.expression
                                    : i18n.certificationPolicies.conditions}
                                value={scopeIsExpression ? <code>{scopeValue}</code> : scopeValue}
                                fullWidth
                            />
                        </DetailList>
                    </CardContent>
                </Card>

                <Card data-testid="section-review-settings">
                    <RowCardHeader>
                        <CardTitle data-testid="section-review-settings-title">{i18n.certificationPolicies.summary.reviewSettingsSection}</CardTitle>
                        {showEdit && (
                            <EditSectionLink
                                to={routes.accessGovernance.certificationPolicies.editAtStep(id!, WizardStep.REVIEW_SETTINGS)}
                                label={String(i18n.certificationPolicies.summary.editReviewSettings)}
                                testId="btn-edit-review-settings"
                            />
                        )}
                    </RowCardHeader>
                    <CardContent>
                        <DetailList columns={2}>
                            <DetailField label={i18n.certificationPolicies.reviewerType} value={reviewerTypeLabel}/>
                            <DetailField label={i18n.certificationPolicies.summary.reviewFrequency} value={frequencyValue}/>
                            <DetailField label={i18n.certificationPolicies.gracePeriodDays} value={policy?.parameter?.gracePeriodDays}/>
                            <DetailField label={i18n.certificationPolicies.nonResponseAction} value={nonResponseLabel}/>
                            <DetailField label={i18n.certificationPolicies.autoRevokeOnOverdue} value={policy?.parameter?.autoRevokeOnOverdue ? i18n.common.yes : i18n.common.no}/>
                            <DetailField
                                label={i18n.certificationPolicies.summary.owner}
                                value={policy?.parameter?.owner
                                    ? <EntityLink value={policy.parameter.owner} testId="policy-owner"/>
                                    : i18n.certificationPolicies.summary.noOwner}
                            />
                        </DetailList>
                    </CardContent>
                </Card>

                {id && (
                    <OwnersSection
                        entityType={OwnersEntityType.certificationPolicies}
                        entityId={id}
                        canEdit={canUpdate}
                        responsibilityOptions={certificationPolicyResponsibilityTypes}
                    />
                )}
            </Stack>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationPolicies.deletePolicy}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationPolicies.deleteConfirm({name: policy?.name ?? ''})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            {/* Create Campaign Dialog */}
            <Dialog open={createCampaignDialog.isOpen} onOpenChange={createCampaignDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationPolicies.createCampaign}</DialogTitle>
                        <DialogDescription>{i18n.certificationPolicies.createCampaignDesc}</DialogDescription>
                    </DialogHeader>
                    <CampaignFormFields
                        value={newCampaign}
                        onChange={setNewCampaign}
                        ownerOptions={campaignOwnerOptions}
                        onOwnerSearch={setCampaignOwnerSearchQuery}
                        ownerSearchLoading={campaignOwnerSearchLoading}
                        selectedOwnerLabel={campaignSelectedOwner?.derivedDisplayName}
                    />
                    {createCampaignMutation.error && <ErrorBanner error={createCampaignMutation.error}/>}
                    <SaveCancelFooter
                        testIdPrefix="campaign"
                        onCancel={createCampaignDialog.close}
                        saving={createCampaignMutation.isPending}
                        disabled={!isCampaignFormValid(newCampaign)}
                        saveLabel={i18n.certificationPolicies.createCampaign}
                        saveType="button"
                        onSave={() => createCampaignMutation.mutate(newCampaign)}
                    />
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
